'use client';

import { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { queryFunctions } from '@hub/common/src/query-functions';
import useCustomTooltip from '@hub/common-components/src/custom-tooltip';

import { Account, Entry } from '@hub/common/src/types/cit-public-api';

import { useCitOriginClient } from '@hub/common-components/src/CitOriginClientProvider';

import SearchingAnimation from '@hub/common-components/src/SearchingAnimation';

import CollectionCard from './CollectionCard';
import EntryCard from '@hub/common-components/src/EntryCard';

export default function AllSearchResults({ searchQuery }: { searchQuery: string }) {
  const citOrigin = useCitOriginClient();

  const { customTooltip, handleHideToolTip, handleShowToolTip } = useCustomTooltip();

  const {
    data: results,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['all-search-results', { searchQuery, citOrigin }],
    queryFn: queryFunctions.getAllSearchResults
  });

  // scroll window back to top when search query changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [searchQuery]);

  if (isLoading || isFetching || !results) {
    return <SearchingAnimation />;
  }

  return (
    <main className='container mx-auto px-4 py-8'>
      {customTooltip}
      {!searchQuery ? (
        <h1 className='text-2xl font-bold'>No search term was provided...</h1>
      ) : (
        <>
          <h1 className='text-2xl font-bold mb-6'>Search Results for &quot;{searchQuery}&quot;</h1>
          {results.entries.length === 0 && results.accounts.length === 0 ? (
            <p className='text-gray-600'>No results found.</p>
          ) : (
            <div>
              <h2 className='text-xl font-bold mt-6'>Collections</h2>
              <hr className='MuiDivider-root border-gray-200' />
              <div
                className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6'
                style={{ paddingTop: '10px' }}
              >
                {results.accounts.map((account: Account) => (
                  <CollectionCard
                    key={account.id}
                    account={account}
                    citOrigin={citOrigin}
                    handleShowToolTip={handleShowToolTip}
                    handleHideToolTip={handleHideToolTip}
                  />
                ))}
              </div>
              <h2 className='text-xl font-bold mt-6'>Entries</h2>
              <hr className='MuiDivider-root border-gray-200' />
              <div
                className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6'
                style={{ paddingTop: '10px' }}
              >
                {results.entries.map((result: Entry) => (
                  <EntryCard
                    key={result.id}
                    entry={result}
                    searchQuery={searchQuery}
                    handleHideToolTip={handleHideToolTip}
                    handleShowToolTip={handleShowToolTip}
                    fromAllSearch={true}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}
